/**
 * Target direct in de tabel invullen (Joris 28 sep: "Excel-vibes").
 *
 * Typ een bedrag, Enter of Tab bewaart en gaat naar de volgende regel (Shift+Enter omhoog),
 * Esc zet terug. Een jaartarget verdeelt over de maanden volgens de standaard; had het account
 * een eigen maandverdeling, dan vragen we eerst of die vervangen mag worden.
 */
import React, { useEffect, useRef, useState } from 'react';
import { InputBase } from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { alsInvoer, euro, leesBedrag } from './budgetLogica';



function focusBuur(input, stap) {
  // Enter springt binnen dezelfde kolom (target of RF02).
  const alle = [...document.querySelectorAll(`input[data-cel-veld="${input.dataset.celVeld}"]`)];
  const volgende = alle[alle.indexOf(input) + stap];
  if (volgende) { volgende.focus(); volgende.select(); }
}

export default function TargetCel({ regel, jaar, magWijzigen, onOpgeslagen, veld = 'target', label }) {
  // Zelfde cel voor Target (volgend jaar) en RF02 (lopend jaar, Joris 28 sep).
  const kop = label || `Target ${jaar}`;
  const bedragVan = (r) => r[veld];
  const [waarde, setWaarde] = useState(alsInvoer(bedragVan(regel)));
  const [bezig, setBezig] = useState(false);
  const gefocust = useRef(false);
  const annuleer = useRef(false);   // Esc: blur mag niet alsnog bewaren
  // Stand bij het begin van de bewerking: een herlaad tijdens het typen mag de versie niet ophogen
  // (anders overschrijven we stil iemand anders). Alleen echt getypte invoer wordt bewaard.
  const start = useRef({ target: bedragVan(regel), versie: regel.versie });
  const gewijzigd = useRef(false);

  // Na een herlaad de nieuwe stand tonen, maar nooit onder iemands vingers door.
  useEffect(() => { if (!gefocust.current && !gewijzigd.current) setWaarde(alsInvoer(bedragVan(regel))); }, [regel[veld]]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!magWijzigen) return euro(bedragVan(regel));

  const bewaar = async () => {
    if (!gewijzigd.current) { setWaarde(alsInvoer(bedragVan(regel))); return; }
    const nieuw = leesBedrag(waarde);
    const { target: startTarget, versie } = start.current;
    const oud = startTarget == null ? null : Number(startTarget);
    const cent = (x) => Math.round(Number(x) * 100);
    if (Number.isNaN(nieuw)) {
      toast.error('Bedrag niet gelezen — typ bijvoorbeeld 25000 of 25.000');
      gewijzigd.current = false;
      setWaarde(alsInvoer(bedragVan(regel)));
      return;
    }
    if (nieuw === oud || (nieuw != null && oud != null && cent(nieuw) === cent(oud))) {
      gewijzigd.current = false;
      setWaarde(alsInvoer(bedragVan(regel)));
      return;
    }
    if (veld === 'target' && regel.maanden_handmatig) {
      const ok = await bevestig({
        titel: 'Eigen maandverdeling vervangen?',
        tekst: `${regel.naam} heeft een eigen verdeling per maand. Een jaartarget verdeelt opnieuw volgens de standaard.`,
        knop: 'Vervangen',
      });
      if (!ok) { gewijzigd.current = false; setWaarde(alsInvoer(bedragVan(regel))); return; }
    }
    setBezig(true);
    try {
      const uit = await budgetApi.bewaar(regel.sleutel, { [veld]: nieuw, versie }, jaar);
      gewijzigd.current = false;
      await onOpgeslagen();
      biedOngedaanAan(`${kop} ${regel.naam} opgeslagen`, async () => {
        await budgetApi.bewaar(regel.sleutel, { ...uit.herstel, versie: uit.versie }, jaar);
        await onOpgeslagen();
      }, { eigenKnop: true });
    } catch (e) {
      if (e.status === 409) {
        // Conceptwaarde laten staan: na het herladen zie je de actuele stand en bewaar je bewust opnieuw.
        toast.error(`${regel.naam} is intussen door iemand anders gewijzigd — je invoer staat er nog; druk opnieuw op Enter om te bewaren`);
        await onOpgeslagen();
        start.current = { target: bedragVan(regel), versie: e.huidig?.versie ?? regel.versie };
      } else {
        toast.error(e.message);
      }
    } finally { setBezig(false); }
  };

  return (
    <InputBase
      value={waarde}
      disabled={bezig}
      placeholder="—"
      onChange={(e) => { gewijzigd.current = true; setWaarde(e.target.value); }}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => {
        gefocust.current = true;
        if (!gewijzigd.current) start.current = { target: bedragVan(regel), versie: regel.versie };
        e.target.select();
      }}
      onBlur={() => { gefocust.current = false; if (annuleer.current) { annuleer.current = false; return; } bewaar(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); const el = e.target; el.blur(); focusBuur(el, e.shiftKey ? -1 : 1); }
        if (e.key === 'Escape') { annuleer.current = true; gewijzigd.current = false; setWaarde(alsInvoer(bedragVan(regel))); e.target.blur(); }
      }}
      inputProps={{ 'data-cel-veld': veld, 'aria-label': `${kop} ${regel.naam}`, inputMode: 'decimal' }}
      sx={{
        width: 118, fontSize: 'inherit', fontWeight: 700,
        '& input': {
          textAlign: 'right', fontVariantNumeric: 'tabular-nums', px: 1, py: 0.5,
          borderRadius: 0.75, border: '1px solid transparent', bgcolor: '#f5f8ff',
          '&:hover': { borderColor: '#c7d2fe' },
          '&:focus': { borderColor: '#4f46e5', bgcolor: '#fff', boxShadow: '0 0 0 2px #e0e7ff' },
        },
      }}
    />
  );
}
